import React from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button
} from "@mui/material";
import produce from "immer";
import cloneDeep from "lodash/cloneDeep";
import SkinAnalysisRow, { defaultAttribute } from "./SkinAnalysisRow";
import { tl } from "../../../components/translate";
import { SkinAnalysisAttribute } from "../../../interfaces/AssessmentInterfaces";
import { SKIN_ANALYSIS_ATTRIBUTES } from "./fields";

interface SkinAnalysisPopUpProps {
  isOpen: boolean;
  onClose: () => void;
  previousAttributes?: SkinAnalysisAttribute[];
  onSave: (data: SkinAnalysisAttribute[]) => void;
}

const getRemainingAttributes = (
  currentAttributes: SkinAnalysisAttribute[],
  allAttributes: SkinAnalysisAttribute[]
) =>
  allAttributes.filter(
    (attr: SkinAnalysisAttribute) =>
      !currentAttributes.some((curr: SkinAnalysisAttribute) => curr.dbKey === attr.dbKey)
  );

const SkinAnalysisPopUp: React.FC<SkinAnalysisPopUpProps> = ({
  isOpen,
  onClose,
  previousAttributes = [],
  onSave
}) => {
  const [attributes, setAttributes] = React.useState<SkinAnalysisAttribute[]>([]);

  React.useEffect(() => {
    if (isOpen) {
      setAttributes(
        previousAttributes?.length
          ? cloneDeep(previousAttributes)
          : cloneDeep(SKIN_ANALYSIS_ATTRIBUTES)
      );
    }
  }, [isOpen, previousAttributes]);

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={isOpen}>
      <DialogTitle
        style={{
          borderBottom: "1px solid #ececec",
          background: "#f9f9f9",
          textTransform: "uppercase"
        }}
      >
        Skin Analysis
      </DialogTitle>
      <DialogContent style={{ padding: "24px 32px" }}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mb: 2
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography style={{ fontWeight: 600, fontSize: "16px" }}>Parameters</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography style={{ fontWeight: 600, fontSize: "16px" }}>Reading</Typography>
          </Box>
          <Box sx={{ width: 40 }} />
        </Box>

        <Box width="100%" mb={2}>
          {attributes.map((attr, index) => (
            <SkinAnalysisRow
              key={attr.dbKey}
              attribute={attr}
              remainingAttributes={getRemainingAttributes(attributes, SKIN_ANALYSIS_ATTRIBUTES)}
              onRemove={() => {
                const updated = produce(attributes, (draft) => {
                  draft.splice(index, 1);
                });
                setAttributes(updated);
              }}
              onChange={(v) => {
                const updated = produce(attributes, (draft) => {
                  draft[index] = v;
                });
                setAttributes(updated);
              }}
            />
          ))}
        </Box>

        <Button
          onClick={() => {
            setAttributes([...attributes, { ...defaultAttribute }]);
          }}
          variant="outlined"
          size="small"
        >
          {tl("assessment.addNew")}
        </Button>
      </DialogContent>
      <DialogActions
        style={{
          background: "#f9f9f9",
          borderTop: "1px solid #ececec",
          padding: "16px 24px"
        }}
      >
        <Button onClick={onClose} color="inherit">
          {tl("assessment.cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            const validAttributes = attributes.filter((attr) => attr.name && attr.reading);
            onSave(validAttributes);
            onClose();
          }}
        >
          {tl("assessment.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SkinAnalysisPopUp;
